import useReportSort from '../../components/reports/useReportSort';
import { useState } from "react";
import {
  FiDownload,
  FiFileText,
  FiFilter,
  FiLoader,
  FiPercent,
  FiRefreshCw,
  FiShield,
} from "react-icons/fi";
import ReportFilterModal from "../../components/reports/ReportFilterModal";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import { hasPermission } from "../../utils/permissions";
const money = (v) =>
  `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const localDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
function save(blob) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "charms-gstr.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}
export default function GstrReportPage() {
  const { accessToken, user } = useAuth();
  const now = new Date();
  const [filters, setFilters] = useState({
    from: localDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: localDate(now),
  });
  const [draftFilters, setDraftFilters] = useState(filters);
  const [showFilters, setShowFilters] = useState(false);
  const [rawReport, setReport] = useState(null);
  const { report, controls: sortControls } = useReportSort(rawReport);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const canGenerate = hasPermission(user, "reports.gstr.generate");
  const canExport = hasPermission(user, "reports.gstr.export");
  const generate = async (nextFilters = filters) => {
    if (!canGenerate) return;
    setLoading(true);
    try {
      setReport(
        await apiRequest("/business-reports/gstr/generate", {
          method: "POST",
          body: nextFilters,
          accessToken,
        }),
      );
      setError("");
    } catch (e) {
      setError(e.message || "Could not generate GSTR report.");
    } finally {
      setLoading(false);
    }
  };
  const openFilters = () => {
    setDraftFilters({ ...filters });
    setShowFilters(true);
  };
  const applyFilters = async () => {
    const nextFilters = { ...draftFilters };
    setFilters(nextFilters);
    await generate(nextFilters);
    setShowFilters(false);
  };
  const exportExcel = async () => {
    setExporting(true);
    try {
      const r = await apiRequest(
        `/business-reports/gstr/export?${new URLSearchParams({ ...filters, format: "XLSX" })}`,
        { accessToken, rawResponse: true },
      );
      save(await r.blob());
    } catch (e) {
      setError(e.message || "Could not export GSTR report.");
    } finally {
      setExporting(false);
    }
  };
  const card = (label, value) => (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="mt-2 text-2xl font-bold text-primary-800">{money(value)}</p>
    </div>
  );
  return (
    <div className="business-report space-y-6">
      <section>
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <div className="flex gap-2 text-xs font-semibold uppercase tracking-[0.18em]">
              <FiPercent /> Business reports
            </div>
            <h1>GSTR Report</h1>
            <p>
              Filing-oriented summary of outward sales, inward purchases, GST
              splits, and HSN values.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={openFilters}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-700"
            >
              <FiFilter /> Filters
            </button>
            <button
              type="button"
              onClick={() => generate()}
              disabled={!canGenerate || loading}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-800 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {loading ? (
                <FiLoader className="animate-spin" />
              ) : (
                <FiRefreshCw />
              )}
              {loading ? "Generating" : "Generate"}
            </button>
            <button
              type="button"
              onClick={exportExcel}
              disabled={!report || !canExport || exporting}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 disabled:opacity-50"
            >
              {exporting ? (
                <FiLoader className="animate-spin" />
              ) : (
                <FiDownload />
              )}
              {exporting ? "Preparing" : "Export Excel"}
            </button>
          </div>
        </div>
      </section>
      {!canGenerate && (
        <p className="mx-4 flex gap-2 rounded-lg bg-amber-50 p-4 text-sm text-amber-800">
          <FiShield /> You do not have permission to generate this report.
        </p>
      )}
      {error && (
        <p className="rounded-xl bg-red-50 p-4 text-sm text-red-700">{error}</p>
      )}
      {report && (
        <>
          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            {card("Outward taxable", report.outward.taxableValue)}
            {card("Outward GST", report.outward.taxAmount)}
            {card("Inward taxable", report.inward.taxableValue)}
            {card("Input GST", report.inward.taxAmount)}
            {card(
              "Net GST",
              Number(report.outward.taxAmount) -
                Number(report.inward.taxAmount),
            )}
          </section>
          <section className="rounded-3xl border border-gray-100 bg-white shadow-sm">
            <div className="border-b p-5">
              <h2 className="text-xl font-bold">HSN summary</h2>
            </div>
            <div className="overflow-x-auto">
                  {sortControls}
              <table className="min-w-[700px] w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <tr>
                    {["HSN", "Taxable value", "GST", "Invoice value"].map(
                      (h) => (
                        <th key={h} className="px-4 py-3">
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {report.hsnSummary.map((row) => (
                    <tr key={row.hsnCode} className="border-t">
                      <td className="px-4 py-3 font-semibold">{row.hsnCode}</td>
                      <td className="px-4 py-3 text-left">
                        {money(row.taxableValue)}
                      </td>
                      <td className="px-4 py-3 text-left">
                        {money(row.taxAmount)}
                      </td>
                      <td className="px-4 py-3 text-left">
                        {money(row.invoiceValue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          <section className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-bold">Export reviewed GSTR</h2>
                <p className="mt-1 text-sm text-gray-500">
                  Excel contains GSTR and HSN summary sheets.
                </p>
              </div>
              <button
                onClick={exportExcel}
                disabled={!canExport || exporting}
                className="inline-flex items-center gap-2 rounded-xl border border-primary-200 px-4 py-2.5 font-semibold text-primary-800 disabled:opacity-50"
              >
                {exporting ? (
                  <FiLoader className="animate-spin" />
                ) : (
                  <FiDownload />
                )}
                {exporting ? "Preparing…" : "Export Excel"}
              </button>
            </div>
          </section>
        </>
      )}
      <ReportFilterModal
        open={showFilters}
        title="GSTR report filters"
        description="Choose the reporting period, then generate a fresh preview."
        loading={loading}
        onApply={applyFilters}
        onClose={() => setShowFilters(false)}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="text-sm font-semibold text-slate-700">
            From date
            <input
              type="date"
              value={draftFilters.from}
              onChange={(e) =>
                setDraftFilters((v) => ({ ...v, from: e.target.value }))
              }
              className="report-input mt-1.5"
            />
          </label>
          <label className="text-sm font-semibold text-slate-700">
            To date
            <input
              type="date"
              value={draftFilters.to}
              onChange={(e) =>
                setDraftFilters((v) => ({ ...v, to: e.target.value }))
              }
              className="report-input mt-1.5"
            />
          </label>
        </div>
      </ReportFilterModal>
    </div>
  );
}
